import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";

const AddNewProgramme = ({ theme }) => {
  const [cityOptions, setCityOptions] = useState([]);
  const [residencyOptions, setResidencyOptions] = useState([]);
  const [universityOptions, setUniversityOptions] = useState([]);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [programmeGroups, setProgrammeGroups] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [toast, setToast] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const initialForm = {
    city_ids: [], // now multiple
    residency_ids: [], // now multiple
    university_ids: [],
    programmeName: "",
    deliveryPatterns: [],
    entryLevel: "",
    intakes: [{ date: "" }],
    status: true,
    programme_group_id: "",
    group_id: "",
  };

  const [formData, setFormData] = useState(initialForm);

  const getUserId = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.id || 0;
  };

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast({ type: "", message: "" }), 4000);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("token");

        const [citiesRes, residenciesRes, dpRes, pgRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/get-cities`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/residencies`, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }),
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/catalog/delivery-patterns`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          ),
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/catalog/get-programme-groups`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          ),
        ]);

        if (!citiesRes.ok || !residenciesRes.ok || !dpRes.ok || !pgRes.ok) {
          throw new Error("Unable to load initial data");
        }

        const { data: cities } = await citiesRes.json();
        const residencies = await residenciesRes.json();
        const deliveryPatterns = await dpRes.json();
        const programmeGroups = await pgRes.json();

        setCityOptions(cities || []);
        setResidencyOptions(residencies || []);
        setDeliveryOptions(deliveryPatterns || []);
        setProgrammeGroups(programmeGroups || []);
      } catch (err) {
        showToast("error", err.message || "Failed to load initial data");
      }
    };

    fetchInitialData();
  }, []);

  // Fetch universities when selected cities or residencies change
  useEffect(() => {
    const fetchUniversities = async () => {
      if (
        formData.city_ids.length === 0 ||
        formData.residency_ids.length === 0
      ) {
        setUniversityOptions([]);
        return;
      }
      try {
        const cityParam = formData.city_ids.join(",");
        const residencyParam = formData.residency_ids.join(",");
        // Endpoint shape: /catalog/universities-by-city/{city_ids}?residency_ids=...
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/catalog/universities-by-city/${cityParam}?residency_ids=${residencyParam}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!res.ok) throw new Error("Unable to load universities");
        console.log(residencyParam, cityParam);
        const list = await res.json();
        setUniversityOptions(list || []);
      } catch (err) {
        showToast("error", err.message || "Failed to fetch universities");
        setUniversityOptions([]);
      }
    };

    fetchUniversities();
  }, [formData.city_ids, formData.residency_ids]);

  useEffect(() => {
    const fetchGroups = async () => {
      if (!formData.programme_group_id) {
        setGroupOptions([]);
        return;
      }

      try {
        setLoadingGroups(true);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-groups/${formData.programme_group_id}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!res.ok) throw new Error("Unable to load groups");

        const groups = await res.json();
        setGroupOptions(groups || []);
      } catch (err) {
        showToast("error", err.message || "Failed to load groups");
        setGroupOptions([]);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchGroups();
  }, [formData.programme_group_id]);

  // generic handler for normal inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // if user uses old single-field inputs, clear universities when city/residency changed
      ...(name === "city_id" || name === "residency_id"
        ? { university_ids: [] }
        : {}),
      ...(name === "programme_group_id" ? { group_id: "" } : {}),
    }));
  };

  const toggleDeliveryPattern = (id) => {
    setFormData((prev) => {
      const alreadySelected = prev.deliveryPatterns.includes(id);
      return {
        ...prev,
        deliveryPatterns: alreadySelected
          ? prev.deliveryPatterns.filter((d) => d !== id)
          : [...prev.deliveryPatterns, id],
      };
    });
  };

  const handleIntakeChange = (idx, val) => {
    const updated = [...formData.intakes];
    updated[idx].date = val;
    setFormData((prev) => ({ ...prev, intakes: updated }));
  };

  const addIntake = () => {
    setFormData((prev) => ({
      ...prev,
      intakes: [...prev.intakes, { date: "" }],
    }));
  };

  const removeIntake = (idx) => {
    if (formData.intakes.length === 1) {
      showToast("error", "At least one intake date is required");
      return;
    }
    const updated = [...formData.intakes];
    updated.splice(idx, 1);
    setFormData((prev) => ({ ...prev, intakes: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      formData.city_ids.length === 0 ||
      formData.residency_ids.length === 0 ||
      formData.university_ids.length === 0 ||
      !formData.programme_group_id ||
      !formData.group_id ||
      !formData.programmeName ||
      formData.deliveryPatterns.length === 0 ||
      !formData.entryLevel ||
      formData.intakes.some((i) => !i.date)
    ) {
      return showToast("error", "Please fill all required fields");
    }

    const payload = {
      city_ids: formData.city_ids.map((id) => Number(id)),
      residency_ids: formData.residency_ids.map((id) => Number(id)),
      university_ids: formData.university_ids.map((id) => Number(id)),
      programme_group_id: Number(formData.programme_group_id),
      group_id: Number(formData.group_id),
      programmeName: formData.programmeName.toString().trim(),
      deliveryPatterns: formData.deliveryPatterns.map((id) => Number(id)),
      entryLevel: formData.entryLevel.toString().trim(),
      intakes: formData.intakes.map((i) => {
        if (!i.date) return "";
        const d = new Date(i.date);
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = String(d.getFullYear()).slice(-2);
        return `${month}/${year}`;
      }),
      status: formData.status,
      createdBy: getUserId(),
    };

    try {
      setSubmitting(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/create-programme`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to create programme");
      }

      showToast("success", "Programme created successfully!");
      setFormData(initialForm);
      setUniversityOptions([]);
    } catch (err) {
      showToast("error", err.message || "Failed to create programme");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white py-8 px-4">
      <div className="fixed top-5 right-5 z-50">
        <AnimatePresence>
          {toast.message && (
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.25 }}
              className={`max-w-xs w-full border rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 ${
                toast.type === "error"
                  ? "bg-red-50 border-red-300 text-red-700"
                  : "bg-green-50 border-green-300 text-green-700"
              }`}
            >
              <span className="text-xl">
                {toast.type === "error" ? "⚠️" : "✅"}
              </span>
              <p className="flex-1 text-sm">{toast.message}</p>
              <button
                onClick={() => setToast({ type: "", message: "" })}
                className="font-bold hover:text-gray-600"
              >
                ×
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-700 to-purple-600 p-6 md:p-8 text-white">
          <motion.h2
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl md:text-3xl font-bold text-center"
          >
            Add New Programme
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-blue-100 text-center mt-2"
          >
            Fill in the details to create a new programme
          </motion.p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 md:p-8"
          noValidate
        >
          <div className="space-y-5">
            {/* Cities (multi) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Cities *
              </label>
              <Select
                isMulti
                options={cityOptions.map((c) => ({
                  value: c.id,
                  label: c.city_name,
                }))}
                value={cityOptions
                  .filter((c) => formData.city_ids.includes(c.id))
                  .map((c) => ({ value: c.id, label: c.city_name }))}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    city_ids: selected ? selected.map((s) => s.value) : [],
                    university_ids: [], // clear universities on city change
                  }))
                }
                placeholder="Select cities..."
                classNamePrefix="react-select"
              />
            </div>

            {/* Residencies (multi) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Residency Status *
              </label>
              <Select
                isMulti
                options={residencyOptions.map((r) => ({
                  value: r.id,
                  label: r.residency,
                }))}
                value={residencyOptions
                  .filter((r) => formData.residency_ids.includes(r.id))
                  .map((r) => ({ value: r.id, label: r.residency }))}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    residency_ids: selected ? selected.map((s) => s.value) : [],
                    university_ids: [], // clear universities on residency change
                  }))
                }
                placeholder="Select residency..."
                classNamePrefix="react-select"
              />
            </div>

            {/* Universities (multi) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Universities *
              </label>
              <Select
                isMulti
                name="university_ids"
                options={universityOptions.map((uni) => ({
                  value: uni.id, // ✅ ID from backend
                  label: uni.university, // ✅ Display name
                }))}
                value={formData.university_ids
                  .map((id) => {
                    const uni = universityOptions.find((u) => u.id === id);
                    return uni
                      ? { value: uni.id, label: uni.university }
                      : null;
                  })
                  .filter(Boolean)}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    university_ids: selected
                      ? selected.map((s) => s.value)
                      : [], // ✅ Only IDs go to backend
                  }))
                }
                isDisabled={
                  formData.city_ids.length === 0 ||
                  formData.residency_ids.length === 0
                }
                placeholder="Select universities..."
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>

            {/* Programme Group */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Programme Group *
              </label>
              <select
                name="programme_group_id"
                value={formData.programme_group_id}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    programme_group_id: e.target.value,
                    group_id: "",
                  }))
                }
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              >
                <option value="">Select a programme group</option>
                {programmeGroups.map((pg) => (
                  <option key={pg.id} value={pg.id}>
                    {pg.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Group */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Group *
              </label>
              <select
                name="group_id"
                value={formData.group_id}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, group_id: e.target.value }))
                }
                disabled={!formData.programme_group_id || loadingGroups}
                required
                className={`w-full px-4 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition ${
                  formData.programme_group_id && !loadingGroups
                    ? "border-gray-300"
                    : "bg-gray-100 cursor-not-allowed"
                }`}
              >
                <option value="">Select a group</option>
                {loadingGroups ? (
                  <option value="" disabled>
                    Loading groups...
                  </option>
                ) : (
                  groupOptions.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.group_name}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Programme Name *
              </label>
              <input
                type="text"
                name="programmeName"
                value={formData.programmeName}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    programmeName: e.target.value,
                  }))
                }
                required
                placeholder="e.g. Data Analytics"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Delivery Patterns *
              </label>
              <div className="flex flex-col gap-2">
                {deliveryOptions.map((d) => (
                  <label key={d.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      value={d.id}
                      checked={formData.deliveryPatterns.includes(d.id)}
                      onChange={() => toggleDeliveryPattern(d.id)}
                      className="h-4 w-4 text-blue-700 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span>{d.pattern}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Entry Level *
              </label>
              <input
                type="text"
                name="entryLevel"
                value={formData.entryLevel}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    entryLevel: e.target.value,
                  }))
                }
                required
                placeholder="e.g. Bachelor"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
              />
            </div>
          </div>

          <div className="md:col-span-2 space-y-5">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Status *
              </label>
              <div className="flex gap-3">
                <label
                  className={`flex-1 text-center px-4 py-2 rounded-lg border cursor-pointer transition ${
                    formData.status === true
                      ? "bg-green-100 border-green-400 text-green-800 shadow-inner"
                      : "bg-white border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    checked={formData.status === true}
                    onChange={() =>
                      setFormData((p) => ({ ...p, status: true }))
                    }
                    className="hidden"
                  />
                  Active
                </label>
                <label
                  className={`flex-1 text-center px-4 py-2 rounded-lg border cursor-pointer transition ${
                    formData.status === false
                      ? "bg-red-100 border-red-400 text-red-800 shadow-inner"
                      : "bg-white border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="status"
                    checked={formData.status === false}
                    onChange={() =>
                      setFormData((p) => ({ ...p, status: false }))
                    }
                    className="hidden"
                  />
                  Inactive
                </label>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Intakes
              </h3>
              <div className="space-y-4">
                {formData.intakes.map((intake, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end"
                  >
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Intake (Month & Year) *
                      </label>
                      <input
                        type="month"
                        value={intake.date}
                        onChange={(e) =>
                          handleIntakeChange(idx, e.target.value)
                        }
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => removeIntake(idx)}
                        disabled={formData.intakes.length === 1}
                        className={`px-4 py-2 rounded-lg border transition ${
                          formData.intakes.length === 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-white text-red-600 border-red-300 hover:bg-red-50"
                        }`}
                      >
                        Remove
                      </button>
                      {idx === formData.intakes.length - 1 && (
                        <button
                          type="button"
                          onClick={addIntake}
                          className="px-4 py-2 bg-white text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition"
                        >
                          Add Another
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-2 flex justify-center mt-6">
            <motion.button
              type="submit"
              disabled={submitting}
              whileHover={{ scale: submitting ? 1 : 1.02 }}
              whileTap={{ scale: submitting ? 1 : 0.98 }}
              className={`px-8 py-3 rounded-full text-white font-semibold shadow-lg transition ${
                submitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-700 hover:bg-blue-800"
              }`}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating...
                </span>
              ) : (
                "Create Programme"
              )}
            </motion.button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNewProgramme;
